"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, type Ref } from "react";
import Link from "next/link";
import { Columns2, LoaderCircle, Mic, Lightbulb, NotebookPen } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import type { ChapterNotesTool } from "./writing-tools-context";
interface MobileWritingToolsMenuProps {
  dictationEnabled: boolean;
  activeTool: "dictation" | "brainstorming" | null;
  triggerRef: Ref<HTMLButtonElement>;
  onSelect: (tool: "dictation" | "brainstorming") => void;
  chapterNotes?: ChapterNotesTool | null;
}
const optionClass = "flex min-h-12 w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm text-zinc-700 hover:bg-zinc-100 focus-visible:outline-2 focus-visible:outline-zinc-400 disabled:pointer-events-none disabled:opacity-40 dark:text-zinc-200 dark:hover:bg-zinc-800";
const iconClass = "size-4 shrink-0 text-zinc-400 dark:text-zinc-500";
const labelClass = "px-3 pb-1.5 pt-2 text-[10px] font-medium uppercase tracking-[0.12em] text-zinc-500 dark:text-zinc-400";

/** The enclosing menu item owns keyboard interaction and the accessible state. */
export function ChapterNotesSwitchIndicator(t0) {
  const $ = _c(9);
  const {
    enabled
  } = t0;
  const t1 = enabled ? "bg-zinc-800 dark:bg-zinc-100" : "bg-zinc-300 dark:bg-zinc-700";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("pointer-events-none inline-flex h-5 w-9 shrink-0 items-center rounded-full p-0.5 transition-colors", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = enabled ? "translate-x-4 bg-white dark:bg-zinc-900" : "translate-x-0 bg-white dark:bg-zinc-400";
  let t4;
  if ($[2] !== t3) {
    t4 = cn("size-4 rounded-full shadow-sm transition-transform", t3);
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t4) {
    t5 = <span className={t4} />;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t2 || $[7] !== t5) {
    t6 = <span aria-hidden={true} className={t2}>{t5}</span>;
    $[6] = t2;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  return t6;
}
export function MobileWritingToolsMenu(t0) {
  const $ = _c(32);
  const {
    dictationEnabled,
    activeTool,
    triggerRef,
    onSelect,
    chapterNotes
  } = t0;
  const [open, setOpen] = useState(false);
  let t1;
  if ($[0] !== onSelect) {
    t1 = function selectTool(tool) {
      setOpen(false);
      onSelect(tool);
    };
    $[0] = onSelect;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const selectTool = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = next => {
      setOpen(next);
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== triggerRef) {
    t3 = <SheetTrigger ref={triggerRef} className="flex min-h-12 w-full items-center justify-center gap-1 px-3 text-xs font-medium focus-visible:outline-2 focus-visible:outline-zinc-400">Writing tools</SheetTrigger>;
    $[3] = triggerRef;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <SheetHeader className="pr-12"><SheetTitle className="text-base font-medium text-zinc-900 dark:text-zinc-100">Writing tools</SheetTitle><SheetDescription className="sr-only">Choose a tool to help with your writing.</SheetDescription></SheetHeader>;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== chapterNotes) {
    t5 = chapterNotes && <><p className={labelClass}>Documents</p><button type="button" role="switch" aria-checked={chapterNotes.enabled} className={optionClass} disabled={chapterNotes.disabled} onClick={() => void chapterNotes.activate()}>{chapterNotes.loading ? <LoaderCircle className={`${iconClass} animate-spin`} aria-hidden={true} /> : <NotebookPen className={iconClass} aria-hidden={true} />}<span className="flex-1">{chapterNotes.loading ? "Adding chapter notes\u2026" : "Chapter notes"}</span>{!chapterNotes.loading && <ChapterNotesSwitchIndicator enabled={chapterNotes.enabled} />}</button><button type="button" role="switch" aria-checked={!!chapterNotes.sideBySide} className={optionClass} disabled={!chapterNotes.enabled || !chapterNotes.toggleSideBySide || chapterNotes.disabled} onClick={() => chapterNotes.toggleSideBySide?.()}><Columns2 className={iconClass} aria-hidden={true} /><span className="flex-1">View side by side</span><ChapterNotesSwitchIndicator enabled={!!chapterNotes.sideBySide} /></button><div className="mx-3 my-2 border-t border-zinc-200 dark:border-zinc-800" /></>;
    $[6] = chapterNotes;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <p className={labelClass}>Voice tools</p>;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== activeTool || $[10] !== dictationEnabled || $[11] !== selectTool) {
    t7 = dictationEnabled ? <button type="button" className={optionClass} onClick={() => selectTool("dictation")}><Mic className={iconClass} aria-hidden={true} /><span><span className="block">{activeTool === "dictation" ? "Turn off Dictation" : "Dictation"}</span><span className="block text-xs text-zinc-500 dark:text-zinc-400">Turn speech into text</span></span></button> : <Link href="/editor/settings" className={optionClass} onClick={() => setOpen(false)}><Mic className={iconClass} aria-hidden={true} />Enable dictation in settings</Link>;
    $[9] = activeTool;
    $[10] = dictationEnabled;
    $[11] = selectTool;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== selectTool) {
    t8 = () => selectTool("brainstorming");
    $[13] = selectTool;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Lightbulb className={iconClass} aria-hidden={true} />;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const t10 = activeTool === "brainstorming" ? "Turn off Brainstorming" : "Brainstorming";
  let t11;
  if ($[16] !== t10) {
    t11 = <span className="block">{t10}</span>;
    $[16] = t10;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <span className="block text-xs text-zinc-500 dark:text-zinc-400">Speak and organize your ideas</span>;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== t11) {
    t13 = <span>{t11}{t12}</span>;
    $[19] = t11;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== t13 || $[22] !== t8) {
    t14 = <button type="button" className={optionClass} onClick={t8}>{t9}{t13}</button>;
    $[21] = t13;
    $[22] = t8;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t14 || $[25] !== t5 || $[26] !== t7) {
    t15 = <SheetContent side="bottom" className="gap-0 rounded-t-2xl border-zinc-200 bg-white pb-[env(safe-area-inset-bottom)] text-zinc-900 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100">{t4}<div className="px-3 pb-5">{t5}{t6}{t7}{t14}</div></SheetContent>;
    $[24] = t14;
    $[25] = t5;
    $[26] = t7;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== open || $[29] !== t15 || $[30] !== t3) {
    t16 = <Sheet open={open} onOpenChange={t2}>{t3}{t15}</Sheet>;
    $[28] = open;
    $[29] = t15;
    $[30] = t3;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  return t16;
}